import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {CommonModule} from "@angular/common";
import {AudioRecordingModule} from "../../../../record/api/component/audio-recording/audio-recording.module";
import {
    AudioRecord,
    AudioRecordingComponent
} from "../../../../record/api/component/audio-recording/audio-recording.component";
import {HttpClient} from "@angular/common/http";
import {environment} from "../../../../../../../../environments/environment";
import {BaseView} from "../../../../../../core/view/base.view";
import {AudioRecordSaveService} from "../../../../record/api/service/save/audioRecordSave.service";
import {AudioRecordSavePayload} from "../../../../record/api/service/save/audioRecordSave.payload";
import {AudioTranscriptionSaveService} from "../../service/audioTranscriptionSave.service";
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatButtonToggleModule} from "@angular/material/button-toggle";
import {TablerIconsModule} from "angular-tabler-icons";
import {MatTooltipModule} from "@angular/material/tooltip";
import {
    HtmlContentDialogComponent, HtmlContentDialogInput
} from "../../../../../dialog/api/component/html-content-dialog/html-content-dialog.component";
import {DialogController} from "../../../../../dialog/dialogController";
import {StickyContainerModule} from "../../../../../../core/component/sticky-container/sticky-container.module";
import {LoadingModule} from "../../../../../../../components/common/loading/loading.module";
import {Moment} from 'moment';

@Component({
    selector: 'app-audio-transcription-v2',
    templateUrl: './audio-transcription-v2.component.html',
    standalone: true,
    imports: [
        CommonModule,
        AudioRecordingModule,
        MatButtonToggleModule,
        ReactiveFormsModule,
        TablerIconsModule,
        MatTooltipModule,
        HtmlContentDialogComponent,
        StickyContainerModule,
        LoadingModule,
    ],
    providers: [
        AudioRecordSaveService,
        AudioTranscriptionSaveService,
    ]
})
export class AudioTranscriptionV2Component extends BaseView implements OnDestroy, AfterViewInit, OnChanges {

    private readonly apiUrl = `wss://eu2.rt.speechmatics.com/v2`;


    @Input() config: AudioTranscriptionConfig;
    @Input() existingTranscription: string;
    @Input() hideAudioButtons = false;
    @Input() type: 'SPEECH' | 'CONVERSATION' = undefined;
    @Input() hideAudioRecordDuration = false;

    @Output() transcriptionEndedEvent = new EventEmitter<AudioTranscriptionV2ComponentTranscription>();
    @Output() transcriptionStartedEvent = new EventEmitter<void>();
    @Output() transcriptionImprovementStartedEvent = new EventEmitter<void>();
    @Output() transcriptionImprovementEndedEvent = new EventEmitter<AudioTranscriptionV2ComponentTranscription>();
    @Output() transcriptionImprovementErrorEvent = new EventEmitter<void>();
    @Output() realtimeTranscriptionUpdatedEvent = new EventEmitter<string>();
    @Output() audioRecordDurationUpdatedEvent = new EventEmitter<Moment>();

    @ViewChild('audioRecordingComponent') audioRecordingComponent: AudioRecordingComponent;
    transcription: string;
    cancelled = false;
    transcriptionLastSpeaker: string;
    partialTranscription: string;
    displayedTranscription: string;
    socket: WebSocket;
    audioRecord: AudioRecord;
    formGroup: FormGroup;
    transcriptionInProgress = false;
    potentialTerms: string[] = [];
    getPotentialTermsInProgress = false;
    improveInProgress = false;


    constructor(private httpClient: HttpClient,
                private recordSaveService: AudioRecordSaveService,
                private transcriptionSaveService: AudioTranscriptionSaveService,
                private formBuilder: FormBuilder,
                private dialogController: DialogController) {
        super();
        this.formGroup = this.formBuilder.group({
            type: new FormControl('SPEECH', Validators.required),
        })
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['type']) {
            if (this.type) {
                this.formGroup.patchValue({
                    type: this.type
                });
            }
        }
    }


    ngOnDestroy() {
        this.closeSocket();
    }

    ngAfterViewInit() {
        this.potentialTerms = [];
        if (this.config.handler) {
            this.getPotentialTermsInProgress = true;
            this.config.handler.getPotentialTerms({})
                .then((result) => {
                this.potentialTerms = result.terms;
                if (this.config.startAutomatically) {
                    this.startTranscription();
                }})
                .finally(() => this.getPotentialTermsInProgress = false);
        } else if (this.config.startAutomatically) {
            this.startTranscription();
        }
    }

    startTranscription() {
        this.reset();
        if (this.socket) {
            this.closeSocket();
        }
        if (!this.config) {
            return;
        }

        this.httpClient.post(environment.webserviceUrl + '/v1/core/audio-transcription/get-token', undefined)
            .toPromise()
            .then((response: any) => {
                console.log('token received');
                const socket = new WebSocket(this.apiUrl + '?jwt=' + response.bearer);
                this.socket = socket;
                socket.onopen = () => {
                    console.log('socket on open');
                    this.transcriptionStartedEvent.emit();
                    this.audioRecordingComponent.startRecording();
                    this.transcriptionInProgress = true;

                    let speakerDiarizationConfig = undefined;
                    const conversationMode = this.isConversation();
                    if (conversationMode === true) {
                        speakerDiarizationConfig = {
                            max_speakers: 2
                        }
                    }

                    // Créer le message de configuration
                    const configMessage = {
                        message: "StartRecognition",
                        audio_format: {
                            type: "file",
                        },
                        transcription_config: {
                            operating_point: "enhanced",
                            enable_entities: true,
                            language: "fr",
                            diarization: conversationMode === true ? 'speaker' : undefined,
                            speaker_diarization_config: speakerDiarizationConfig,
                            enable_partials: true,
                            additional_vocab: Array.from(new Set(this.potentialTerms))
                        }
                    };
                    // Envoyer le message de configuration initial
                    if (socket.readyState === WebSocket.OPEN) {
                        socket.send(JSON.stringify(configMessage));
                    }
                };

                socket.onclose = event => {
                    console.log('socket on close');
                    this.stopRecording(false);
                    this.reset();
                };
                socket.onerror = event => {
                    console.log('socket on error');
                    this.stopRecording(false);
                    this.reset();
                };

                socket.onmessage = (event) => {
                    console.log('socket on message', event);
                    const message = JSON.parse(event.data);
                    // console.log('Received message:', message);
                    if (message.message === 'Error') {
                        this.stopRecording(false);
                        this.closeSocket();
                        this.reset();
                    } else if (message.message === 'EndOfTranscript') {
                        console.log('EndOfTranscript', this.transcription);
                        const transcription = this.transcription;
                        this.stopRecording(false, 0);
                        if (this.audioRecord && transcription) {
                            AudioRecordSavePayload.fromRecord(this.audioRecord, 'DOCUMENT_CATEGORY_AUDIO_TRANSCRIPTION').then(value => {
                                this.recordSaveService.save(value).then(record => {
                                    this.transcriptionSaveService.save({
                                        audioRecordId: record.summary.id,
                                        context: this.config.context,
                                        transcription,
                                        providerValue: 'AUDIO_TRANSCRIPTION_PROVIDER_SPEECHMATICS',
                                        id: undefined
                                    })
                                })
                            })

                        }

                        if (this.config.handler && !this.cancelled && this.transcription) {
                            this.transcriptionImprovementStartedEvent.emit();
                            this.improveInProgress = true;
                            this.config.handler.improveTranscription({
                                conversation: this.isConversation(),
                                existingText: this.existingTranscription,
                                transcription: this.transcription
                            })
                                .then((result) => {
                                    this.transcriptionEndedEvent.emit({
                                        transcription: result.text,
                                        conversation: this.isConversation()
                                    });
                                    this.transcriptionImprovementEndedEvent.emit({
                                        transcription: result.text,
                                        conversation: this.isConversation()
                                    })
                                    this.reset();
                                })
                                .catch((e) => {
                                    this.transcriptionImprovementErrorEvent.emit();
                                    this.toastControl.presentError(e, 'La transcription n\'a pu être améliorée');
                                    console.log('Error improvement', this.transcription);
                                    this.emitTranscriptionEndedEvent(this.transcription);
                                })
                                .finally(() => this.improveInProgress = false)
                        } else {
                            console.log('Emit outside improvement');
                            this.emitTranscriptionEndedEvent(this.transcription)
                            this.reset();
                        }
                    } else if (message.message === 'AddTranscript' || message.message === 'AddPartialTranscript') {
                        let currentTranscript = '';
                        message.results.forEach(segment => {
                            if (segment.alternatives.length > 0) {
                                const alternative = segment.alternatives[0];
                                if (message.message === 'AddTranscript' && alternative.speaker) {
                                    console.log([alternative.speaker, this.transcriptionLastSpeaker, this.transcriptionLastSpeaker !== alternative.speaker])
                                    if (this.transcriptionLastSpeaker !== alternative.speaker) {
                                        let speakerContent = '';
                                        if (this.transcription?.length > 0 || currentTranscript?.length > 0) {
                                            speakerContent = '<br/>';
                                        }
                                        speakerContent += '[' + alternative.speaker + ']<br/>';
                                        currentTranscript += speakerContent;
                                    }
                                    this.transcriptionLastSpeaker = alternative.speaker;
                                }
                                if (alternative?.content) {
                                    currentTranscript += alternative.content + ' ';
                                }
                            }
                        });

                        if (message.message === 'AddPartialTranscript') {
                            this.partialTranscription = currentTranscript;
                        } else {
                            this.transcription += currentTranscript;
                            this.partialTranscription = undefined;
                        }
                        this.refreshDisplayedTranscription();
                    }
                };
            })
            .catch((e) => this.toastControl.presentError(e, 'Lancement de l\'enregistement audio impossible'))
    }

    emitTranscriptionEndedEvent(transcription: string) {
        this.transcriptionEndedEvent.emit({
            transcription: this.existingTranscription ? (this.existingTranscription + '<br/>' + transcription) : transcription,
            conversation: this.isConversation()
        });
    }

    reset() {
        this.transcriptionInProgress = false;
        this.audioRecord = undefined;
        this.transcription = '';
        this.partialTranscription = '';
        this.cancelled = false;
        this.refreshDisplayedTranscription();
    }

    refreshDisplayedTranscription() {
        this.displayedTranscription = '';
        if (this.transcription) {
            this.displayedTranscription += this.transcription;
        }
        if (this.partialTranscription) {
            this.displayedTranscription += this.partialTranscription;
        }

        this.displayedTranscription = this.displayedTranscription.replace('<br/>', '');
        this.displayedTranscription = this.displayedTranscription.replace('[S1]', '');
        this.displayedTranscription = this.displayedTranscription.replace('[S2]', '');
        this.realtimeTranscriptionUpdatedEvent.emit(this.displayedTranscription);
    }

    audioChunkRecorded(chunk: Blob) {
        if (this.isSocketOpen()) {
            const reader = new FileReader();
            reader.onload = () => {
                this.socket.send(reader.result as ArrayBuffer);
            };
            reader.readAsArrayBuffer(chunk);
        }
    }

    stopRecording(saveRecord: boolean, timeout = 500) {
        // In case of the websocket fails immediately, let time to the recording component to finish.
        if (timeout > 0) {
            setTimeout(() => this.audioRecordingComponent?.stopRecording(saveRecord), 500);
        } else {
            this.audioRecordingComponent?.stopRecording(saveRecord);
        }

    }

    cancel() {
        this.cancelled = true;
        setTimeout(() => this.audioRecordingComponent?.stopRecording(false), 500);
    }

    stopTranscription() {
        if (this.isSocketOpen()) {
            setTimeout(() => {
                if (this.isSocketOpen()) {
                    this.socket.send(JSON.stringify({
                        message: "EndOfStream",
                        last_seq_no: 50
                    }))
                }
            }, 100);
        }
    }

    audioRecorded(event: AudioRecord) {
        // console.log('audio recorded');
        this.audioRecord = event;
    }

    closeSocket() {
        if (this.isSocketNotClosedOrClosing()) {
            try {
                this.socket.close();
            } catch (e) {
                console.log(e);
            }
        }
    }

    isSocketOpen() {
        return this.socket && this.socket.readyState === WebSocket.OPEN;
    }

    isSocketNotClosedOrClosing() {
        return this.socket && this.socket.readyState !== WebSocket.CLOSED && this.socket.readyState !== WebSocket.CLOSING;
    }

    isConversation(): boolean {
        return this.formGroup.value.type === 'CONVERSATION';
    }

    openConversationInstructionsDialog() {
        this.openInstructionDialog(this.config.conversationHTMLInstructions)
    }

    openSpeechInstructionsDialog() {
        this.openInstructionDialog(this.config.speechHTMLInstructions)
    }

    openInstructionDialog(instructions: string) {
        const input: HtmlContentDialogInput = {
            textAsHtml: instructions
        }
        this.dialogController.open(HtmlContentDialogComponent, input, {
            title: 'Instructions',
        });
    }

}

export class AudioTranscriptionConfig {
    context: string;
    audioContentValue: string;
    startAutomatically = false;
    enableConversationOption: boolean = false;
    showPreview :boolean;
    handler?: AudioTranscriptionHandler<any>;
    speechHTMLInstructions?: string;
    conversationHTMLInstructions?: string;

}

export abstract class AudioTranscriptionHandler<SetupPayload> {
    private setupDone = false;

    protected abstract coreSetup(payload: SetupPayload);
    protected abstract coreGetPotentialTerms(payload: AudioTranscriptionHandlerGetTermsPayload): Promise<AudioTranscriptionHandlerGetTermsResult>;
    protected abstract coreImproveTranscription(payload: AudioTranscriptionHandlerImproveTranscriptionPayload): Promise<AudioTranscriptionHandlerImproveResult>;

    setup(payload: SetupPayload) {
        this.setupDone = true;
        this.coreSetup(payload);
    }

    getPotentialTerms(payload: AudioTranscriptionHandlerGetTermsPayload): Promise<AudioTranscriptionHandlerGetTermsResult> {
        if (!this.setupDone) {
            return new Promise<AudioTranscriptionHandlerGetTermsResult>((resolve, reject) => {
                reject('setup has not been done');
            })
        }
        return this.coreGetPotentialTerms(payload);
    }

    improveTranscription(payload: AudioTranscriptionHandlerImproveTranscriptionPayload): Promise<AudioTranscriptionHandlerImproveResult> {
        if (!this.setupDone) {
            return new Promise<AudioTranscriptionHandlerImproveResult>((resolve, reject) => {
                reject('setup has not been done');
            })
        }
        return this.coreImproveTranscription(payload);
    }

}

export class AudioTranscriptionHandlerGetTermsPayload {
}

export class AudioTranscriptionHandlerGetTermsResult {
    terms: string[];
}

export class AudioTranscriptionHandlerImproveTranscriptionPayload {
    transcription: string;
    conversation: boolean;
    existingText?: string;
}

export class AudioTranscriptionHandlerImproveResult {
    text: string;
}


export class AudioTranscriptionV2ComponentTranscription {
    transcription: string;
    conversation: boolean;
}
